// src/pages/Partenaires/PartenairesAdminPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  FiActivity,
  FiArrowDown,
  FiArrowUp,
  FiEdit2,
  FiExternalLink,
  FiEye,
  FiImage,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiUsers,
} from "react-icons/fi";

import { useUser } from "../../contexts/UserContext";
import PartenaireDrawer from "./PartenaireDrawer";

import {
  getAllPartenaires,
  addPartenaire,
  updatePartenaire,
  updatePartenaireOrder,
  updatePartenaireActivation,
  updatePartenairePhoto,
  deletePartenaire,
} from "./../../services/partenairesServices";

const FALLBACK_LOGO = "/img/partenaire-placeholder.png";

const PAGE =
  "min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50";

const CARD =
  "rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900";

const INPUT =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950";

const toAbsolutePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (String(photoUrl).startsWith("http")) return photoUrl;

  const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const clean = String(photoUrl).replaceAll("\\", "/").replace(/^\/+/, "");

  return `${base}/${clean}`;
};

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

const isPartnerActive = (partner) =>
  partner?.is_active === true ||
  partner?.is_active === 1 ||
  partner?.is_active === "1";

const computeNextOrder = (items) => {
  const max = Math.max(0, ...items.map((partner) => Number(partner.ordre) || 0));
  return max + 1;
};

const getStoredUserId = () => {
  try {
    const direct = localStorage.getItem("user_id");
    if (direct) return Number(direct);

    const userInfo = JSON.parse(localStorage.getItem("userInfo"));
    return userInfo?.userId ?? userInfo?.id ?? userInfo?.user_id ?? null;
  } catch {
    return null;
  }
};

export default function PartenairesAdminPage() {
  const { user } = useUser();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [editing, setEditing] = useState(null);

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const ctxUserId = useMemo(() => {
    return user?.userId ?? user?.id ?? user?.user_id ?? null;
  }, [user]);

  const userId = ctxUserId ?? getStoredUserId();

  const refresh = useCallback(async () => {
    setLoading(true);

    try {
      const all = await getAllPartenaires();
      const list = Array.isArray(all) ? all : [];

      const scoped = userId
        ? list.filter((partner) => Number(partner.user_id) === Number(userId))
        : list;

      setItems(
        scoped
          .slice()
          .sort((a, b) => (Number(a.ordre) || 0) - (Number(b.ordre) || 0))
      );
    } catch (error) {
      console.error("[Partenaires] refresh error:", error);
      toast.error("Erreur lors du chargement des partenaires.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const stats = useMemo(() => {
    const active = items.filter(isPartnerActive).length;

    return {
      total: items.length,
      active,
      inactive: items.length - active,
    };
  }, [items]);

  const filteredItems = useMemo(() => {
    const search = normalizeText(q.trim());

    let rows = [...items];

    if (search) {
      rows = rows.filter((partner) => {
        const searchable = [
          partner.nom,
          partner.link_url,
          partner.photo_url,
          partner.ordre,
        ]
          .map(normalizeText)
          .join(" ");

        return searchable.includes(search);
      });
    }

    if (statusFilter === "active") {
      rows = rows.filter(isPartnerActive);
    }

    if (statusFilter === "inactive") {
      rows = rows.filter((partner) => !isPartnerActive(partner));
    }

    return rows;
  }, [items, q, statusFilter]);

  const openCreate = () => {
    setDrawerMode("create");
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (partner) => {
    setDrawerMode("edit");
    setEditing(partner);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
    setDrawerMode("create");
  };

  const handleSave = async (data, file) => {
    try {
      const nom = String(data?.nom || "").trim();

      if (!nom) {
        toast.error("Le nom est obligatoire.");
        return;
      }

      if (!userId) {
        toast.error("user_id introuvable. Vérifiez votre connexion.");
        return;
      }

      if (drawerMode === "create") {
        const payload = {
          nom,
          link_url: String(data?.link_url || "").trim(),
          photo_url: String(data?.photo_url || "").trim(),
          is_active: Boolean(data?.is_active),
          ordre: computeNextOrder(items),
          user_id: Number(userId),
        };

        const created = await addPartenaire(payload);

        if (!created?.id) {
          toast.error("Création échouée : aucun identifiant reçu.");
          return;
        }

        if (file) {
          await updatePartenairePhoto(created.id, file);
        }

        toast.success("Partenaire créé.");
      }

      if (drawerMode === "edit") {
        if (!editing?.id) return;

        await updatePartenaire(editing.id, {
          nom,
          link_url: String(data?.link_url || "").trim(),
          photo_url: String(data?.photo_url || "").trim(),
          is_active: Boolean(data?.is_active),
        });

        if (file) {
          await updatePartenairePhoto(editing.id, file);
        }

        toast.success("Partenaire modifié.");
      }

      closeDrawer();
      await refresh();
    } catch (error) {
      console.error("[Partenaires] save error:", error);
      toast.error(
        drawerMode === "create"
          ? "Création impossible."
          : "Modification impossible."
      );
    }
  };

  const handleToggleActive = async (partner) => {
    const nextValue = !isPartnerActive(partner);

    setItems((prev) =>
      prev.map((item) =>
        item.id === partner.id ? { ...item, is_active: nextValue } : item
      )
    );

    try {
      await updatePartenaireActivation(partner.id, nextValue);
      toast.success(nextValue ? "Partenaire activé." : "Partenaire désactivé.");
    } catch (error) {
      console.error("[Partenaires] toggleActive error:", error);
      toast.error("Activation impossible.");
      refresh();
    }
  };

  const handleDelete = async (partner) => {
    const confirmed = window.confirm(
      `Supprimer définitivement le partenaire "${partner.nom}" ?`
    );

    if (!confirmed) return;

    try {
      await deletePartenaire(partner.id);
      toast.success("Partenaire supprimé.");
      await refresh();
    } catch (error) {
      console.error("[Partenaires] delete error:", error);
      toast.error("Suppression impossible.");
    }
  };

  const handleMove = async (id, direction) => {
    const index = items.findIndex((partner) => partner.id === id);
    if (index < 0) return;

    const nextIndex = direction === "up" ? index - 1 : index + 1;
    if (nextIndex < 0 || nextIndex >= items.length) return;

    const copy = items.slice();
    [copy[index], copy[nextIndex]] = [copy[nextIndex], copy[index]];

    const normalized = copy.map((partner, orderIndex) => ({
      ...partner,
      ordre: orderIndex + 1,
    }));

    setItems(normalized);

    try {
      await updatePartenaireOrder(
        normalized.map((partner) => ({
          id: partner.id,
          ordre: partner.ordre,
        }))
      );

      toast.success("Ordre enregistré.");
    } catch (error) {
      console.error("[Partenaires] order error:", error);
      toast.error("Ordre non enregistré.");
      refresh();
    }
  };

  return (
    <div className={PAGE}>
      <main className="mx-auto w-full max-w-[1680px] px-3 pb-12 pt-[130px] sm:px-5 md:pt-[155px]">
        <div className="space-y-6">
          <PartenairesHero
            stats={stats}
            loading={loading}
            onRefresh={refresh}
            onCreate={openCreate}
          />

          <PartenairesFilters
            q={q}
            setQ={setQ}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            total={filteredItems.length}
          />

          {loading ? (
            <LoadingGrid />
          ) : filteredItems.length === 0 ? (
            <EmptyState onCreate={openCreate} />
          ) : (
            <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredItems.map((partner) => {
                const originalIndex = items.findIndex(
                  (item) => item.id === partner.id
                );

                return (
                  <PartenaireCard
                    key={partner.id}
                    partner={partner}
                    index={originalIndex}
                    total={items.length}
                    onEdit={openEdit}
                    onDelete={handleDelete}
                    onToggleActive={handleToggleActive}
                    onMove={handleMove}
                  />
                );
              })}
            </section>
          )}
        </div>
      </main>

      <PartenaireDrawer
        open={drawerOpen}
        mode={drawerMode}
        initial={editing}
        onClose={closeDrawer}
        onSave={handleSave}
      />
    </div>
  );
}

function PartenairesHero({ stats, loading, onRefresh, onCreate }) {
  return (
    <section className="overflow-hidden rounded-[36px] bg-slate-950 shadow-xl dark:bg-slate-900">
      <div className="relative p-5 sm:p-7 lg:p-8">
        <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-indigo-500/25 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-40 w-40 rounded-full bg-pink-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-blue-500/15 blur-3xl" />

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white/80">
              <FiUsers />
              Partenaires
            </div>

            <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Gestion des partenaires
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-300 sm:text-base">
              Ajoutez vos sponsors, organisez leur ordre d’affichage et publiez
              les logos qui apparaîtront sur les pages publiques des
              compétitions.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onCreate}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-bold text-slate-950 shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-100"
            >
              <FiPlus />
              Ajouter
            </button>

            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-4 py-3 text-sm font-bold text-white ring-1 ring-white/15 transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FiRefreshCw className={loading ? "animate-spin" : ""} />
              Actualiser
            </button>
          </div>
        </div>

        <div className="relative mt-7 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <HeroStat icon={FiUsers} label="Total" value={stats.total} />
          <HeroStat icon={FiActivity} label="Actifs" value={stats.active} />
          <HeroStat icon={FiImage} label="Inactifs" value={stats.inactive} />
        </div>
      </div>
    </section>
  );
}

function HeroStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-white/[0.08] p-4 text-white shadow-sm backdrop-blur">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
          <Icon />
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-white/60">
            {label}
          </div>
          <div className="mt-0.5 text-2xl font-bold">{value}</div>
        </div>
      </div>
    </div>
  );
}

function PartenairesFilters({
  q,
  setQ,
  statusFilter,
  setStatusFilter,
  total,
}) {
  return (
    <section className={`${CARD} p-4 sm:p-5`}>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Vos partenaires
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {total} résultat{total > 1 ? "s" : ""} affiché
            {total > 1 ? "s" : ""}.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
        <div className="relative lg:col-span-8">
          <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Rechercher par nom, lien, ordre..."
            className={`${INPUT} pl-11`}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          className={`${INPUT} lg:col-span-4`}
        >
          <option value="all">Tous les statuts</option>
          <option value="active">Actifs uniquement</option>
          <option value="inactive">Inactifs uniquement</option>
        </select>
      </div>
    </section>
  );
}

function PartenaireCard({
  partner,
  index,
  total,
  onEdit,
  onDelete,
  onToggleActive,
  onMove,
}) {
  const logo = toAbsolutePhotoUrl(partner.photo_url) || FALLBACK_LOGO;
  const active = isPartnerActive(partner);
  const isFirst = index <= 0;
  const isLast = index >= total - 1;
  const hasLink = Boolean(String(partner.link_url || "").trim());

  const openPartnerLink = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!partner.link_url) return;

    window.open(partner.link_url, "_blank", "noopener,noreferrer");
  };

  return (
    <article className="group overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-slate-900">
      <div className="relative h-44 overflow-hidden bg-white dark:bg-slate-950">
        <div className="absolute left-4 top-4 z-10 flex flex-wrap gap-2">
          <StatusBadge active={active} />
          <span className="rounded-full bg-slate-100/95 px-3 py-1.5 text-xs font-bold text-slate-800 backdrop-blur dark:bg-slate-800/95 dark:text-slate-100">
            Ordre {partner.ordre ?? index + 1}
          </span>
        </div>

        <div className="flex h-full w-full items-center justify-center px-8 py-8">
          <img
            src={logo}
            alt={partner.nom || "Partenaire"}
            className="max-h-full max-w-full object-contain transition duration-300 group-hover:scale-[1.03]"
            onError={(event) => {
              event.currentTarget.src = FALLBACK_LOGO;
            }}
          />
        </div>
      </div>

      <div className="border-t border-slate-100 p-4 dark:border-slate-800">
        <div className="mb-3">
          <h2 className="line-clamp-2 text-lg font-bold leading-tight text-slate-900 dark:text-white">
            {partner.nom || "Partenaire sans nom"}
          </h2>
        </div>

        {hasLink ? (
          <button
            type="button"
            onClick={openPartnerLink}
            className="inline-flex max-w-full items-center gap-2 rounded-2xl bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700 transition hover:bg-indigo-100 dark:bg-indigo-950/70 dark:text-indigo-300 dark:hover:bg-indigo-950"
          >
            <FiExternalLink className="shrink-0" />
            <span className="truncate">{partner.link_url}</span>
          </button>
        ) : (
          <div className="inline-flex rounded-2xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            Aucun lien renseigné
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onMove(partner.id, "up");
            }}
            disabled={isFirst}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-3 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          >
            <FiArrowUp />
            Monter
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onMove(partner.id, "down");
            }}
            disabled={isLast}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-3 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          >
            <FiArrowDown />
            Descendre
          </button>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onEdit(partner);
            }}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-3 py-2.5 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
          >
            <FiEdit2 />
            Modifier
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggleActive(partner);
            }}
            className={`inline-flex items-center justify-center gap-2 rounded-2xl px-3 py-2.5 text-sm font-bold text-white transition hover:-translate-y-0.5 ${
              active
                ? "bg-amber-600 hover:bg-amber-700"
                : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            {active ? "Désactiver" : "Activer"}
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onDelete(partner);
            }}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 px-3 py-2.5 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-rose-700"
          >
            <FiTrash2 />
            Supprimer
          </button>
        </div>
      </div>
    </article>
  );
}

function StatusBadge({ active }) {
  return (
    <span
      className={`rounded-full px-3 py-1.5 text-xs font-bold backdrop-blur ${
        active
          ? "bg-emerald-100 text-emerald-700"
          : "bg-slate-100 text-slate-700"
      }`}
    >
      {active ? "Actif" : "Inactif"}
    </span>
  );
}

function LoadingGrid() {
  return (
    <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
        >
          <div className="h-44 animate-pulse bg-slate-200 dark:bg-slate-800" />
          <div className="space-y-3 p-4">
            <div className="h-4 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="h-4 w-1/2 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="h-10 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
          </div>
        </div>
      ))}
    </section>
  );
}

function EmptyState({ onCreate }) {
  return (
    <section className="flex flex-col items-center justify-center rounded-[30px] border border-dashed border-slate-300 bg-white px-5 py-16 text-center dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-[24px] bg-slate-100 text-2xl text-slate-500 dark:bg-slate-800 dark:text-slate-300">
        <FiEye />
      </div>

      <h3 className="text-lg font-bold text-slate-900 dark:text-white">
        Aucun partenaire
      </h3>

      <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-500 dark:text-slate-400">
        Ajoutez vos partenaires ou sponsors pour les afficher sur les pages
        publiques des compétitions.
      </p>

      <button
        type="button"
        onClick={onCreate}
        className="mt-5 inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-700"
      >
        <FiPlus />
        Ajouter un partenaire
      </button>
    </section>
  );
}